"use client";

import { useRef, useState } from "react";
import { useShallowSearchParam, setShallowSearchParams } from "@/lib/hooks/use-shallow-search-param";
import { locationTabs, resolveLocationTab, type HouseId, type LocationTabId } from "@/lib/conditions/location-houses";

export type HouseTabKeyboardTarget = { index: number; id: LocationTabId };

export function houseTabTargetForKey(key: string, index: number, withMap = false): HouseTabKeyboardTarget | null {
  const tabs = locationTabs(withMap);
  let next: number;
  if (key === "ArrowRight") next = (index + 1) % tabs.length;
  else if (key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
  else if (key === "Home") next = 0;
  else if (key === "End") next = tabs.length - 1;
  else return null;
  return { index: next, id: tabs[next].id };
}

export function HouseTabs({ panels, toolbar, mapPanel }: { panels: Record<HouseId, React.ReactNode>; toolbar?: React.ReactNode; mapPanel?: React.ReactNode }) {
  const withMap = mapPanel != null;
  const tabs = locationTabs(withMap);
  const active = resolveLocationTab(useShallowSearchParam("tab"), withMap);
  const [mapVisited, setMapVisited] = useState(active === "map");
  if (active === "map" && !mapVisited) setMapVisited(true);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function select(id: LocationTabId) {
    setShallowSearchParams({ tab: id }, { mode: "merge", history: "push" });
    const top = document.getElementById("location-sections")?.getBoundingClientRect().top;
    if (top !== undefined && top < 64) {
      document.getElementById("location-sections")?.scrollIntoView({ block: "start" });
    }
  }

  function onKey(e: React.KeyboardEvent, idx: number) {
    const target = houseTabTargetForKey(e.key, idx, withMap);
    if (target === null) return;
    e.preventDefault();
    select(target.id);
    tabRefs.current[target.index]?.focus();
  }

  return (
    <div id="location-sections" className="max-w-[1100px] mx-auto scroll-mt-16 px-5 md:px-12">
      <div id="location-navigation" className="sticky top-16 z-30 -mx-5 scroll-mt-16 border-b border-border bg-warm-bg/95 px-5 py-3 backdrop-blur md:-mx-3 md:px-3">
      {toolbar && <div className="mb-2.5">{toolbar}</div>}
      <div role="tablist" aria-label="Location sections"
        className="flex gap-1 rounded-[14px] border border-border bg-white p-1 shadow-sm">
        {tabs.map((h, i) => {
          const on = h.id === active;
          return (
            <button key={h.id} role="tab" id={`tab-${h.id}`} aria-selected={on}
              aria-controls={`panel-${h.id}`} tabIndex={on ? 0 : -1}
              ref={(el) => { tabRefs.current[i] = el; }}
              onClick={() => select(h.id)} onKeyDown={(e) => onKey(e, i)}
              className={`min-h-11 min-w-11 flex-1 rounded-[10px] px-1 py-2.5 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal ${
                on ? "bg-navy text-white shadow-sm" : "text-text-body hover:bg-warm-bg"
              }`}>
              <span className={`block font-bold md:text-[15px] ${withMap ? "text-[11px] min-[390px]:text-[13px]" : "text-[13px]"}`}>{h.label}</span>
              <span className={`mt-0.5 text-[9px] md:text-[11px] ${withMap ? "hidden min-[390px]:block" : "block min-[390px]:text-[10px]"} ${on ? "text-white/80" : "text-text-muted"}`}>{h.description}</span>
            </button>
          );
        })}
      </div>
      </div>
      {tabs.map((h) => (
        <div key={h.id} role="tabpanel" id={`panel-${h.id}`} aria-labelledby={`tab-${h.id}`}
          hidden={h.id !== active} tabIndex={h.id === active ? 0 : undefined}>
          {h.id === "map" ? (mapVisited || active === "map" ? mapPanel : null) : h.id === active ? panels[h.id] : null}
        </div>
      ))}
    </div>
  );
}
